import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, eur, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
         AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Cake, Utensils, Wine, Star, Phone, Mail, Calendar, Heart, Award, AlertTriangle, Trash2, User, UserPlus } from "lucide-react";
import { getCustomerStage, STAGE_ICON, STAGE_LABEL, isVipEffective, vipReason, marketingStateLabel, marketingStateStyle } from "@/lib/customerStage";
import CustomerTimeline from "@/components/CustomerTimeline";
import CustomerInsights, { CustomerBanners } from "@/components/CustomerInsights";
import CustomerListsChips from "@/components/CustomerListsChips";

const SEGMENT_STYLE = {
  nuovo:      "bg-emerald-100 text-emerald-800 border-emerald-300",
  attivo:     "bg-sky-100 text-sky-800 border-sky-300",
  dormiente:  "bg-amber-100 text-amber-800 border-amber-300",
  vip:        "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-300",
};

const BADGE_STYLE = "text-xs rounded-full border px-2 py-0.5 bg-bronze-50 border-bronze-200 text-bronze-800";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT",
    { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(iso).slice(0, 16).replace("T", " "); }
}

export default function AdminCustomerDetail() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [form, setForm] = useState({
    name: "", surname: "", phone: "", email: "",
    birthday: "", language: "it",
    allergies: "", notes: "",
    marketing_consent: false, vip_manual: false,
  });
  const [saving, setSaving] = useState(false);
  const [firstConfirmOpen, setFirstConfirmOpen] = useState(false);
  const [secondConfirmOpen, setSecondConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/customers/${cid}`);
      setC(r.data);
      setForm({
        name: r.data?.name || "",
        surname: r.data?.surname || "",
        phone: r.data?.phone || "",
        email: r.data?.email || "",
        birthday: r.data?.birthday || "",
        language: r.data?.language || "it",
        allergies: (r.data?.allergies || []).join(", "),
        notes: r.data?.internal_notes || "",
        marketing_consent: !!r.data?.marketing_consent,
        vip_manual: !!r.data?.vip_manual,
      });
    } catch (e) { toast.error(formatApiError(e)); }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/customers/${cid}`, {
        name: form.name.trim() || null,
        surname: form.surname.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        birthday: form.birthday || null,
        language: form.language || "it",
        allergies: form.allergies.split(",").map((s) => s.trim()).filter(Boolean),
        internal_notes: form.notes.trim() || null,
        marketing_consent: form.marketing_consent,
        vip_manual: form.vip_manual,
      });
      toast.success("Scheda salvata");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  // Beach Menu PRD § 12 (v2) — HARD DELETE totale e irreversibile.
  // Nessun branch di anonimizzazione: cliente, ordini e figli spariscono
  // definitivamente. Doppia conferma: alert generico → digitazione "ELIMINA".
  const executeDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/customers/${cid}`, {
        data: { confirmation: "ELIMINA" },
      });
      toast.success("Cliente e dati associati eliminati definitivamente");
      setSecondConfirmOpen(false);
      setConfirmText("");
      // Segnale + redirect: la lista clienti si aggiorna al mount senza refresh manuale.
      window.dispatchEvent(new CustomEvent("sb-customers-changed"));
      nav("/admin/customers", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  if (!c) return <div className="p-6 text-ink-500">Caricamento…</div>;

  return (
    <div className="space-y-4" data-testid="crm-detail-page">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/admin/customers" className="btn-outline text-xs py-1 px-3" data-testid="crm-detail-back">
          <ArrowLeft className="w-3 h-3 mr-1" /> Clienti
        </Link>
        <div>
          <h1 className="font-serif text-3xl flex items-center gap-3 flex-wrap">
            {c.stage && (
              <span title={STAGE_LABEL[c.stage]} data-testid={`crm-stage-${c.stage}`}
                    className="text-3xl leading-none">
                {STAGE_ICON[c.stage]}
              </span>
            )}
            <span>{[c.name, c.surname].filter(Boolean).join(" ") || "Cliente"}</span>
            {isVipEffective(c) && (
              <span title={vipReason(c) || "VIP"} data-testid="crm-vip-star"
                    className="text-2xl text-amber-500">⭐</span>
            )}
          </h1>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            {c.stage && (
              <span className="text-[11px] uppercase tracking-wider rounded-full border px-2 py-0.5 bg-emerald-50 border-emerald-200 text-emerald-900">
                {STAGE_ICON[c.stage]} {STAGE_LABEL[c.stage]}
              </span>
            )}
            <span className={`text-[10px] uppercase tracking-wider rounded-full border px-2 py-0.5 ${SEGMENT_STYLE[c.segment] || SEGMENT_STYLE.attivo}`}
                  data-testid={`crm-segment-badge-${c.segment}`}>
              {c.segment}
            </span>
            {isVipEffective(c) && (
              <span className="text-[10px] uppercase tracking-wider rounded-full border px-2 py-0.5 bg-amber-100 border-amber-300 text-amber-900"
                    data-testid="crm-vip-badge">
                ⭐ VIP · {vipReason(c)}
              </span>
            )}
            {(c.badges || []).map((b) => (
              <span key={b} className={BADGE_STYLE} data-testid={`crm-badge-${b}`}>{b}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Banners v4 (sopra anagrafica) */}
      <CustomerBanners customer={c} />

      {/* P0-6 FASE 2G+ · Liste CRM (Sarabi source of truth, nessun sync WABA) */}
      <section className="rounded-2xl border border-sand-300 bg-white p-3">
        <CustomerListsChips customerId={cid} />
      </section>

      {/* Snapshot */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-sand-300 bg-white p-3">
          <div className="text-xs text-ink-500">Ordini totali</div>
          <div className="text-2xl font-serif" data-testid="crm-detail-orders">{c.orders_count}</div>
        </div>
        <div className="rounded-xl border border-sand-300 bg-white p-3">
          <div className="text-xs text-ink-500">Speso</div>
          <div className="text-2xl font-serif">{eur(c.total_spent_cents)}</div>
        </div>
        <div className="rounded-xl border border-sand-300 bg-white p-3">
          <div className="text-xs text-ink-500">Ø ticket</div>
          <div className="text-2xl font-serif">{eur(c.avg_ticket_cents)}</div>
        </div>
        <div className="rounded-xl border border-bronze-200 bg-bronze-50 p-3">
          <div className="text-xs text-bronze-700 flex items-center gap-1"><Award className="w-3 h-3" /> Punti Loyalty</div>
          <div className="text-2xl font-serif text-bronze-900" data-testid="crm-detail-points">{c.loyalty_points}</div>
        </div>
      </section>

      {/* Contact + editable anagrafica (CRM Upgrade v2) */}
      <section className="grid md:grid-cols-2 gap-3">
        <div className="rounded-2xl border border-sand-300 bg-white p-4 space-y-3" data-testid="crm-anagrafica">
          <h2 className="font-semibold flex items-center gap-1"><User className="w-4 h-4" /> Anagrafica</h2>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-ink-500">Nome</label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                     data-testid="crm-name-input" />
            </div>
            <div>
              <label className="text-xs text-ink-500">Cognome</label>
              <Input value={form.surname} onChange={(e) => setForm({...form, surname: e.target.value})}
                     data-testid="crm-surname-input" />
            </div>
          </div>
          <div>
            <label className="text-xs text-ink-500 flex items-center gap-1"><Phone className="w-3 h-3" /> Telefono</label>
            <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                   placeholder="+39…" data-testid="crm-phone-input" />
          </div>
          <div>
            <label className="text-xs text-ink-500 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</label>
            <Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                   type="email" data-testid="crm-email-input" />
          </div>
          <div className="text-xs text-ink-500 flex items-center gap-1"><Calendar className="w-3 h-3" />
            Ultima visita: {fmtDate(c.last_order_at)}
            {c.days_since_last_visit !== null && (
              <span className="ml-1">({c.days_since_last_visit} gg fa)</span>
            )}
          </div>
          <div className="text-xs text-ink-500">Primo ordine: {fmtDate(c.first_order_at)}</div>

          {/* Marketing state */}
          <div className="pt-2 border-t border-sand-200">
            <div className="text-xs text-ink-500 mb-1">Consenso marketing</div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[11px] uppercase tracking-wider rounded-full border px-2 py-0.5 ${marketingStateStyle(c.marketing_state)}`}
                    data-testid={`crm-marketing-${c.marketing_state}`}>
                {marketingStateLabel(c.marketing_state)}
              </span>
              {c.marketing_consent_at && (
                <span className="text-[11px] text-ink-500" data-testid="crm-marketing-at">
                  {fmtDate(c.marketing_consent_at)}
                  {c.marketing_consent_source ? ` · ${c.marketing_consent_source}` : ""}
                </span>
              )}
            </div>
            <label className="mt-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.marketing_consent}
                     onChange={(e) => setForm({...form, marketing_consent: e.target.checked})}
                     data-testid="crm-marketing-toggle" />
              <span>Consenso marketing attivo</span>
            </label>
          </div>

          {/* VIP */}
          <div className="pt-2 border-t border-sand-200">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.vip_manual}
                     onChange={(e) => setForm({...form, vip_manual: e.target.checked})}
                     data-testid="crm-vip-manual-toggle" />
              <span>Cliente VIP manuale <span className="text-amber-500">⭐</span></span>
            </label>
            <div className="text-[11px] text-ink-500 mt-1">
              {vipReason(c) || `Diventa VIP automatico al raggiungimento di ${eur(200000)}`}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-sand-300 bg-white p-4 space-y-3">
          <h2 className="font-semibold flex items-center gap-1"><Cake className="w-4 h-4" /> Scheda</h2>
          <div>
            <label className="text-xs text-ink-500">Compleanno</label>
            <Input type="date" value={form.birthday} onChange={(e) => setForm({...form, birthday: e.target.value})}
                   data-testid="crm-birthday-input" />
          </div>
          <div>
            <label className="text-xs text-ink-500">Lingua</label>
            <select value={form.language} onChange={(e) => setForm({...form, language: e.target.value})}
                    className="w-full rounded-md border border-sand-300 bg-white px-3 py-2 text-sm"
                    data-testid="crm-language-input">
              <option value="it">Italiano</option>
              <option value="en">English</option>
              <option value="de">Deutsch</option>
              <option value="fr">Français</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-ink-500">Allergie (separate da virgola)</label>
            <Input value={form.allergies} onChange={(e) => setForm({...form, allergies: e.target.value})}
                   placeholder="es. glutine, arachidi" data-testid="crm-allergies-input" />
          </div>
          <div>
            <label className="text-xs text-ink-500">Note interne</label>
            <Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}
                      rows={4} placeholder="Es. preferisce tavolo vicino alla finestra"
                      data-testid="crm-notes-input" />
          </div>
          <Button size="sm" onClick={save} disabled={saving} data-testid="crm-notes-save">
            {saving ? "Salvo…" : "Salva scheda"}
          </Button>
        </div>
      </section>

      {/* Favorites */}
      {c.favorites?.length > 0 && (
        <section className="rounded-2xl border border-sand-300 bg-white p-4">
          <h2 className="font-semibold mb-2 flex items-center gap-1"><Heart className="w-4 h-4 text-rose-600" /> Preferiti (Top 5)</h2>
          <ul className="grid md:grid-cols-2 gap-2 text-sm">
            {c.favorites.map((f) => (
              <li key={f.product_id} data-testid={`crm-fav-${f.product_id}`}
                  className="rounded-lg border border-sand-200 p-2 flex items-center justify-between gap-2">
                <div>
                  <div className="font-medium">{f.product_name}</div>
                  {f.category_name && <div className="text-xs text-ink-500">{f.category_name}</div>}
                </div>
                <div className="text-xs text-ink-500 text-right">
                  <div><b>{f.quantity}×</b> ordinato</div>
                  <div>{eur(f.spent_cents)}</div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Insight cliente (v4) */}
      <CustomerInsights cid={cid} />

      {/* Timeline (CRM Upgrade v3) */}
      <CustomerTimeline cid={cid} />

      {/* Order history */}
      <section className="rounded-2xl border border-sand-300 bg-white p-4">
        <h2 className="font-semibold mb-2 flex items-center gap-1"><Utensils className="w-4 h-4" /> Cronologia ordini · {c.orders?.length || 0}</h2>
        {c.orders?.length === 0 ? (
          <div className="text-xs text-ink-500 italic">Nessun ordine ancora.</div>
        ) : (
          <ol className="border-l border-sand-300 space-y-1.5 ml-1">
            {c.orders.map((o) => (
              <li key={o.id} data-testid={`crm-order-${o.id}`}
                  className="ml-3 relative rounded-lg border border-sand-200 px-3 py-2 flex items-center justify-between">
                <span className="absolute -left-[7px] top-3 w-2.5 h-2.5 rounded-full bg-bronze-500 border-2 border-white" />
                <div className="min-w-0">
                  <div className="font-medium">
                    #{o.code}
                    <span className="text-xs text-ink-500 ml-2">{fmtDate(o.created_at)}</span>
                  </div>
                  <div className="text-xs text-ink-500">
                    {o.item_count} articoli · {o.station_name || "—"} · <b>{o.status}</b>
                  </div>
                </div>
                <div className="font-serif">{eur(o.total_cents)}</div>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* Beach Menu PRD § 12 (v2) — Danger Zone · HARD DELETE totale.
          Sezione separata, in fondo alla pagina. Nessuna anonimizzazione. */}
      <section
        className="mt-8 rounded-2xl border-2 border-red-400 bg-red-50/60 p-5"
        data-testid="crm-danger-zone"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-red-700 flex-shrink-0 mt-0.5" strokeWidth={2} />
          <div className="flex-1">
            <h2 className="font-semibold text-red-900 text-base">Zona pericolosa</h2>
            <p className="text-sm text-red-900/90 mt-1 leading-relaxed">
              Questa operazione eliminerà definitivamente il cliente, <b>TUTTI gli ordini</b> e <b>TUTTE le prenotazioni</b> associate. L'operazione è irreversibile.
            </p>
            <Button
              variant="destructive"
              className="mt-4 bg-red-700 hover:bg-red-800 text-white"
              onClick={() => setFirstConfirmOpen(true)}
              data-testid="crm-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Elimina cliente
            </Button>
          </div>
        </div>
      </section>

      {/* Primo dialog: avviso generico */}
      <AlertDialog open={firstConfirmOpen} onOpenChange={setFirstConfirmOpen}>
        <AlertDialogContent data-testid="crm-delete-first-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Stai per eliminare definitivamente questo cliente.
            </AlertDialogTitle>
            <AlertDialogDescription>
              Verranno cancellati <b>in modo irreversibile</b>: il record cliente,
              tutti gli <b>{c.orders_count || 0} ordini</b> associati, le righe ordine,
              i punti loyalty, i preferiti e ogni altro dato collegato.
              Nessun record "Anonimo" verrà creato in sostituzione.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="crm-delete-first-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-700 hover:bg-red-800 text-white"
              onClick={(e) => {
                e.preventDefault();
                setFirstConfirmOpen(false);
                setConfirmText("");
                setSecondConfirmOpen(true);
              }}
              data-testid="crm-delete-first-continue"
            >
              Continua
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Secondo dialog: digitazione ELIMINA */}
      <Dialog open={secondConfirmOpen} onOpenChange={(o) => {
        if (!o) { setConfirmText(""); }
        setSecondConfirmOpen(o);
      }}>
        <DialogContent data-testid="crm-delete-second-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Conferma finale
            </DialogTitle>
            <DialogDescription>
              Per procedere digita <b className="font-mono text-red-900">ELIMINA</b> qui sotto.
              L'operazione è irreversibile.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Digita ELIMINA"
            autoFocus
            autoComplete="off"
            data-testid="crm-delete-confirm-input"
            className="font-mono uppercase"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setSecondConfirmOpen(false); setConfirmText(""); }}
              disabled={deleting}
              data-testid="crm-delete-second-cancel"
            >
              Annulla
            </Button>
            <Button
              className="bg-red-700 hover:bg-red-800 text-white"
              onClick={executeDelete}
              disabled={confirmText.trim() !== "ELIMINA" || deleting}
              data-testid="crm-delete-confirm-btn"
            >
              {deleting ? "Eliminazione…" : "Elimina definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
